import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { logAssessmentOperation } from "@/utils/assessmentUtils";

type RiskAssessment = Database['public']['Tables']['folder_risk_assessments']['Row'];
type Folder = Database['public']['Tables']['folders']['Row'];

export const linkAssessment = async (
  assessmentId: string,
  name: string,
  description: string,
  folderId: string,
  projectId: string
): Promise<RiskAssessment> => {
  logAssessmentOperation("API_LINK_ASSESSMENT", {
    assessmentId,
    name,
    folderId,
    projectId
  });

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  if (!userId) {
    throw new Error("Användare inte inloggad");
  }

  const { data, error } = await supabase
    .from("folder_risk_assessments")
    .insert({
      assessment_id: assessmentId,
      name: name,
      description: description || null,
      folder_id: folderId,
      project_id: projectId, // CRITICAL: Always include project_id
      type: "riskbedomning",
      data: {}, // Initialize with empty data structure
      created_by: userId,
      updated_by: userId
    })
    .select()
    .single();

  if (error) {
    logAssessmentOperation("API_LINK_ERROR", { error: error.message });
    throw new Error(`Kunde inte länka riskbedömning: ${error.message}`);
  }

  logAssessmentOperation("API_LINK_SUCCESS", {
    id: data.id,
    assessmentId: data.assessment_id,
    projectId: data.project_id
  });

  return data;
};

export const createFolder = async (
  name: string,
  description: string | null,
  projectId: string,
  parentFolderId: string | null
): Promise<Folder> => {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  if (!userId) {
    throw new Error("Användare inte inloggad");
  }

  const { data, error } = await supabase
    .from("folders")
    .insert({
      name: name.trim(),
      description: description?.trim() || null,
      parent_folder_id: parentFolderId,
      project_id: projectId,
      created_by: userId
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Kunde inte skapa mapp: ${error.message}`);
  }

  return data;
};

export const deleteFolder = async (folderId: string): Promise<void> => {
  const { error } = await supabase
    .from("folders")
    .delete()
    .eq("id", folderId);

  if (error) {
    throw error;
  }
};

export const deleteAssessment = async (assessmentId: string): Promise<void> => {
  logAssessmentOperation("DELETE_ASSESSMENT_START", { assessmentId });
  
  try {
    // First try to find the assessment in folder_risk_assessments table
    const { data: riskAssessment, error: riskFetchError } = await supabase
      .from("folder_risk_assessments")
      .select("*")
      .eq("id", assessmentId)
      .maybeSingle();

    // If not found in risk assessments, try SKR assessments table
    let assessment: any = riskAssessment; // Use any type to handle both assessment types
    let isSkrAssessment = false;
    
    if (!riskAssessment && !riskFetchError) {
      console.log("🔍 Assessment not found in risk_assessments, checking SKR table...");
      const { data: skrAssessment, error: skrFetchError } = await supabase
        .from("skr_assessments")
        .select("*")
        .eq("id", assessmentId)
        .maybeSingle();
      
      if (skrFetchError) {
        logAssessmentOperation("DELETE_SKR_FETCH_ERROR", { error: skrFetchError.message });
        throw skrFetchError;
      }
      
      if (skrAssessment) {
        assessment = skrAssessment;
        isSkrAssessment = true;
        console.log("✅ Found SKR assessment to delete:", { id: assessmentId, name: skrAssessment.name });
      }
    } else if (riskFetchError) {
      logAssessmentOperation("DELETE_FETCH_ERROR", { error: riskFetchError.message });
      throw riskFetchError;
    }

    if (!assessment) {
      logAssessmentOperation("DELETE_NOT_FOUND", { assessmentId });
      throw new Error("Bedömning hittades inte");
    }

    console.log(`🗑️ Deleting ${isSkrAssessment ? 'SKR' : 'Risk'} assessment:`, { 
      id: assessmentId, 
      name: assessment.name,
      isSkrAssessment 
    });

    // Delete related data first to maintain referential integrity
    // Use the assessment_id for cleanup (both types should have this)
    const assessmentIdForCleanup = assessment.assessment_id || assessmentId;
    
    // Delete status history
    const { error: statusError } = await supabase
      .from("risk_assessment_status_history")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (statusError) {
      console.warn("Could not delete status history:", statusError);
    }

    // Delete communication records
    const { error: commError } = await supabase
      .from("risk_assessment_communication")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (commError) {
      console.warn("Could not delete communication records:", commError);
    }

    // Delete transfer items using project_id and folder_id
    const { error: transferError } = await supabase
      .from("risk_transfer_items")
      .delete()
      .eq("project_id", assessment.project_id)
      .eq("folder_id", assessment.folder_id);

    if (transferError) {
      console.warn("Could not delete transfer items:", transferError);
    }

    // Delete question status tracking
    const { error: trackingError } = await supabase
      .from("question_status_tracking")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (trackingError) {
      console.warn("Could not delete question tracking:", trackingError);
    }

    // Delete workflow audit trail
    const { error: auditError } = await supabase
      .from("workflow_audit_trail")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (auditError) {
      console.warn("Could not delete audit trail:", auditError);
    }

    // Delete user roles for this assessment
    const { error: rolesError } = await supabase
      .from("risk_assessment_user_roles")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (rolesError) {
      console.warn("Could not delete user roles:", rolesError);
    }

    // Delete notifications
    const { error: notifError } = await supabase
      .from("workflow_notifications")
      .delete()
      .eq("assessment_id", assessmentIdForCleanup);

    if (notifError) {
      console.warn("Could not delete notifications:", notifError);
    }

    // Finally, delete from the appropriate table
    let deleteError;
    if (isSkrAssessment) {
      console.log("🗑️ Deleting from skr_assessments table...");
      const { error } = await supabase
        .from("skr_assessments")
        .delete()
        .eq("id", assessmentId);
      deleteError = error;
    } else {
      console.log("🗑️ Deleting from folder_risk_assessments table...");
      const { error } = await supabase
        .from("folder_risk_assessments")
        .delete()
        .eq("id", assessmentId);
      deleteError = error;
    }

    if (deleteError) {
      logAssessmentOperation("DELETE_MAIN_ERROR", { error: deleteError.message });
      throw deleteError;
    }

    // Clean up local storage backups for this assessment
    if (assessment.project_id && assessment.folder_id) {
      try {
        const keys = Object.keys(localStorage);
        const backupKeys = keys.filter(key => 
          key.includes(`${assessment.project_id}_${assessment.folder_id}`) &&
          (key.includes('risksammanstallning') || key.includes('backup'))
        );
        
        backupKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn("Could not remove localStorage item:", key);
          }
        });
        
        logAssessmentOperation("DELETE_CLEANUP_SUCCESS", { 
          removedBackups: backupKeys.length 
        });
      } catch (e) {
        console.warn("Could not clean up localStorage:", e);
      }
    }

    logAssessmentOperation("DELETE_SUCCESS", { 
      assessmentId,
      assessment_id: assessment.assessment_id
    });

  } catch (error) {
    logAssessmentOperation("DELETE_ERROR", { 
      assessmentId, 
      error: error.message 
    });
    throw error;
  }
};

export const fetchParentFolder = async (folderId: string): Promise<Folder | null> => {
  const { data, error } = await supabase
    .from("folders")
    .select("*")
    .eq("id", folderId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
};

export const fetchFolders = async (projectId: string, parentFolderId: string | null): Promise<Folder[]> => {
  const query = supabase
    .from("folders")
    .select("*")
    .eq("project_id", projectId)
    .order("name");

  if (parentFolderId) {
    query.eq("parent_folder_id", parentFolderId);
  } else {
    query.is("parent_folder_id", null);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
};

export const fetchAssessments = async (folderId: string): Promise<RiskAssessment[]> => {
  const { data, error } = await supabase
    .from("folder_risk_assessments")
    .select("*")
    .eq("folder_id", folderId)
    .order("name");

  if (error) {
    throw error;
  }

  return data || [];
};
